import { defineStore } from 'pinia'
import { reactive, computed, ref } from 'vue'
import { useWebAppStore } from '@/stores/webApp'
import {
  AWARDS_ENUM,
  AWARDS_STATUS_ENUM,
  TIERS_ENUM,
  CARD_CODE_TARS_ENUM,
  COBRAND_AIRLINES_PARTNER_CODE_ENUM,
  CONTEXT_TYPE_ADDRESS_ENUM,
  MEMBER_GIFT_STATE,
  STATUS_RULE,
  CONTEXT_TYPE_ENUM,
  USAGE_TYPE_ADDRESS_ENUM,
  USAGE_TYPE_PHONE_ENUM,
  NPAI_ENUM,
  EXPLORER_AWARD_CATEGORY,
  EXPLORER_AWARD_STATUS
} from '@/constants/loyalty'
import { IBIS_CARD, ALL_VOYAGEUR_CARD, ALL_EXPLORER_CARD } from '@/constants/subscriptions'
import type { CreateStatusGiftResponse } from '@/services/status-gift-service'
import type { ComputedRef } from 'vue'
import { axiosApi } from '@/services/http/axiosApi'
import { API_CUSTOMER_CONTACT } from '@/constants/urls'
import { userFieldsString, filterOperations } from '@/stores/constants'
import { isAfterDateNow, isTodayAtLeastNDaysAfterToday } from '@/helpers/date-helpers'
import { capitalize } from '@/helpers/formatters'
import {
  ALL_PLUS_PRODUCT_CODE,
  ALL_SIGNATURE_PLAN,
  ALL_SIGNATURE_STATUS,
  SUBSCRIPTION_NAME
} from '@/constants/subscriptions'
import { getCurrency } from '@/helpers/currency'
import type { AxiosResponse } from 'axios'

interface User {
  firstName?: string
  lastName?: string
}

export interface Operation {
  cancelled: boolean
  description: string
  nightsCount: number
  offerCode: string
  offerName: string
  operationDate: string
  points: number
  statusPoints: number
  transactionDate: Date
  aberrantCode: string
  type: string
}

export interface StayDetail {
  tarsBookingNumber: string
  checkOutDate: string
  checkInDate: string
  hotelName: string
  redemptionDate: string
}

export interface Award {
  expirationDate: string
  stayDetail: StayDetail
  status: AWARDS_STATUS_ENUM | EXPLORER_AWARD_STATUS
  category?: {
    // for now this is only available for allPlusExplorer
    id: EXPLORER_AWARD_CATEGORY
    label: string
  }
  name?: string
  triggedDate?: string
  redeemedAt?: {
    hotelCode: string
    hotelName: string
  }
  details: string
  awardId: string
  startDate: string
  redemptionDescription: string
  terms: string
}

export interface AwardsObject {
  numberOfRemainingAwards: number
  awardTypeName: string
  type: AWARDS_ENUM
  awards: {
    award: Award[]
  }
}

export interface CoBrandedCard {
  partnerCode: COBRAND_AIRLINES_PARTNER_CODE_ENUM
  partnerName: string
  programmeName: string
  subscriptionDate: string
}

export interface SubscribedBonus {
  type: string
  criteriaBonus: {
    bookingBeginDate: string
    bookingEndDate: string
    stayBeginDate: string
    stayEndDate: string
  }
  informationBonus: {
    wiseId: string
  }
}

export interface StatusGiftObject {
  giftType?: string
  eligibility?: string
  isEligible?: boolean
  isConsumed?: boolean
  hasNodeStatusGift?: boolean
  givenCardProduct?: {
    productTier?: string
  }
  giftExpirationDate?: string
  giftDate?: string
  beneficiaryStatusExpirationDate?: string
  gifts?: {
    beneficiaryCardNumber: string
    beneficiaryPmid: string
    beneficiaryStatusExpirationDate: string
    donorCardNumber: string
    eligibility: string
    giftExpirationDate: string
    givenCardProduct: CardProduct
    giftDate?: string
    cancelled?: boolean
  }[]
}

export interface StatusMatches {
  statusMatchCode: string
  statusMatchLabel: string
  nbNightsRequired: number
  nbNightsAcquired: number
  addDate: string
  endDate: string
  status: string
  tierGiven: number
  offerCompleted: boolean
  updateDate: string
}

export type Rrule = 'FREQ=MONTHLY;INTERVAL=1' | 'FREQ=MONTHLY;INTERVAL=3' | 'FREQ=YEARLY;INTERVAL=1'

export interface AllSignatureSubscription {
  status: ALL_SIGNATURE_STATUS
  rrule: Rrule
  amount: number
  current_period_ends: string
  plan_code: ALL_SIGNATURE_PLAN
  subscription_id: number
  currency: string
  change_to_pricing_option_id: string
}

export interface PhoneMedium {
  isPrimary: boolean
  phoneNumber: string
  phonePrefix: string
  phoneType: CONTEXT_TYPE_ENUM
  requestedPhone?: {
    phone: string
    prefix: string
    remainingAttempts: number
    expirationDate: string
  }
}

export interface ContactMedium {
  emailContact?: {
    email: string
    isPrimary: boolean
    emailRequested?: {
      email: string
      requestedDate: string
      tracking: {
        createdDate: string
        createdBySystem: string
        updatedDate: string
        updatedBySystem: string
      }
    }
  }
  contextType: CONTEXT_TYPE_ADDRESS_ENUM | CONTEXT_TYPE_ENUM
  id: string
  isNpai: boolean
  isOptOut: boolean
  mediumUsages?: {
    mediumUsage: [
      {
        isOptOut: boolean
        usageType: string
      }
    ]
  }
  postalAddress?: {
    address1: string
    address2?: string
    city: string
    countryCode: string
    isAsBilling: boolean
    isPrimary: boolean
    zipCode: string
    intraCommunityVat?: string
  }
  tracking?: {
    createdBySystem: string
    createdDate: string
    updatedBySystem: string
    updatedDate: string
  }
  telephoneNumber?: PhoneMedium
}
export interface CardProduct {
  active: boolean
  cardCodeTARS: CARD_CODE_TARS_ENUM
  productBin: string
  productCode: string
  productLabel: string
  productType: string
  productTier?: TIERS_ENUM
}
export interface SubscriptionCard {
  beginDate: string
  cardNumber: string
  cardProduct: CardProduct
  duplicationStatus: string
  endDate: string
  isLastActiveCard: string
  termsAndConditionsAcceptanceDate: string
}

interface IndividualSubscription {
  id: string
  subscribeDate: string
  unsubscribeDate?: string
  unsubscribeReason?: string
  optin: boolean
  origine: string
  code: string
}
export interface IndividualLegalIdentification {
  id: string | null
  type: string | null
  nationalityCode: string | null
  number: string | null
  issuingPlace: string | null
  expirationDate: Date | null
  issuedDate: Date | null
}

export enum FreeNightStatus {
  REDEEMABLE = 'REDEEMABLE',
  REDEEMED = 'REDEEMED',
  EXPIRED = 'EXPIRED',
  BOOKED = 'BOOKED',
  BLOCKED = 'BLOCKED'
}

export interface FreeNight {
  creationDate: string
  expirationDate: string
  id: string
  modificationDate: string
  status: FreeNightStatus
  updateStatusDate: string
  checkInDate?: string
}

export interface GetContactResponseBodyInterface {
  contactMediums: {
    contactMedium: ContactMedium[]
  }
  loyalty: {
    balances: {
      currentNightsBalance: number
      currentStaysBalance: number
      nbNightsToNextTiering: number
      nbPoints: number
      nbPointsEarnLast12Months: number
      nbPointsToNextTiering: number
      nightsSpentOnTierUpdate: number
      pointsEarnedOnTierUpdate: number
      fastTrackedStatusNights?: number
      pointsAmountForStays: {
        amount: number
        currencyCode: string
        currencySymbol: string
      }
    }
    memberInfo: {
      locked?: boolean
      displayBurnOnline?: boolean
      memberPhysicalCard?: boolean
      logo?: string
      pointsExpirationDate?: string
      statusPointsExpirationDate?: string
      meetingPlannerAuthorizationEndDate?: string
      loyaltyTermsAndConditionsAcceptancedate?: string
      nextTiering?: string
      allTypesAwardEligibility?: string
      userHashKey?: string
      status?: string
      fullEnrolmentDate?: string
      preEnrollExpirationDate?: string
      enrolledBeforeGDPRCompliancy?: boolean
      enrollCompletionReminderEmailSent?: boolean
    }
    operations?: {
      operation: Operation[]
    }
    loyaltyCards: {
      cardSupport?: {
        phone: string
        priceDescription: string
        disponibility: string
      }
      card: {
        cardNumber: string
        isLastActiveCard: boolean
        endDate: string
        duplicationStatus: string
        cardProduct: {
          cardCodeTARS: CARD_CODE_TARS_ENUM
          productTier: TIERS_ENUM
          productLabel: string
        }
        statusRule: STATUS_RULE
      }[]
    }
    statusGifts?: StatusGiftObject[]
    statusMatches?: StatusMatches[]
    awardTypes: {
      awardType: AwardsObject[]
    }
    coBrandedCards?: {
      coBrandedCard: CoBrandedCard[]
    }
    subscribedBonuses?: {
      subscribedBonus: SubscribedBonus[]
    }
    allSignatureSubscriptions?: AllSignatureSubscription[]
    memberGifts?: {
      digitalMemberGiftEligibility: MEMBER_GIFT_STATE
      reason?: string
    }
    subscriptionCards?: {
      card: SubscriptionCard[]
    }
    freenights: {
      freenight: FreeNight[]
    }
  }
  individual: {
    pmid: string
    individualName: {
      firstName: string
      lastName: string
      civility: string
    }
    individualSubscriptions: {
      individualSubscription: IndividualSubscription[]
    }
    nationality?: string
    placeOfBirth?: string
    birthDate?: string
    individualContracts?: {
      accessCode: string
      companySCCode: string
      contractId: string
      id: string
    }
    hasWallet?: boolean
    isNameUpdateAllowed?: boolean
    individualLegalIdentifications?: {
      individualLegalIdentification: IndividualLegalIdentification[]
    }
  }
  statusGiftResponse?: CreateStatusGiftResponse
  authentication?: {
    idpInformation: {
      lastAuthenticationDate: string
      lastPasswordUpdateDate: string
    }
  }
}

export const useUserStore = defineStore('user', () => {
  const raw: GetContactResponseBodyInterface = reactive({
    contactMediums: {
      contactMedium: [
        {
          contextType: CONTEXT_TYPE_ADDRESS_ENUM.PERSONAL,
          id: '',
          isNpai: false,
          isOptOut: false
        }
      ]
    },
    loyalty: {
      balances: {
        currentNightsBalance: 0,
        currentStaysBalance: 0,
        nbNightsToNextTiering: 0,
        nbPoints: 0,
        nbPointsEarnLast12Months: 0,
        nbPointsToNextTiering: 0,
        nightsSpentOnTierUpdate: 0,
        pointsEarnedOnTierUpdate: 0,
        pointsAmountForStays: {
          amount: 0,
          currencyCode: '',
          currencySymbol: ''
        }
      },
      loyaltyCards: {
        card: [
          {
            cardNumber: '',
            cardProduct: {
              cardCodeTARS: CARD_CODE_TARS_ENUM.A1,
              productTier: TIERS_ENUM.CLASSIC,
              productLabel: ''
            },
            duplicationStatus: '',
            isLastActiveCard: false,
            endDate: '',
            statusRule: STATUS_RULE.PROGRESSING
          }
        ]
      },
      memberInfo: {
        pointsExpirationDate: ''
      },
      awardTypes: {
        awardType: [
          {
            numberOfRemainingAwards: 0,
            type: AWARDS_ENUM.DIN,
            awardTypeName: '',
            awards: {
              award: []
            }
          },
          {
            numberOfRemainingAwards: 0,
            type: AWARDS_ENUM.SNU,
            awardTypeName: '',
            awards: {
              award: []
            }
          }
        ]
      },
      freenights: {
        freenight: []
      }
    },
    individual: {
      pmid: '',
      individualName: {
        firstName: '',
        lastName: '',
        civility: ''
      },
      individualSubscriptions: {
        individualSubscription: []
      },
      nationality: '',
      hasWallet: false
    },
    subscriptionCards: {
      card: []
    }
  })

  const pmid = computed(() => raw.individual.pmid)
  const user: User = reactive({})
  const userCurrency = ref('')
  const userRawPromise = ref<Promise<AxiosResponse>>()

  const setUserCurrency = (value: string) => {
    userCurrency.value = value
  }

  const setUser = (value: User) => {
    Object.assign(user, value)
  }

  const setRaw = (value: object) => {
    Object.assign(raw, value)
    const webAppStore = useWebAppStore()
    webAppStore.setUserInfoReadyState()
  }

  const refreshUser = async () => {
    userRawPromise.value = axiosApi.get(API_CUSTOMER_CONTACT, {
      params: {
        currency: getCurrency(),
        fields: userFieldsString,
        filter: filterOperations
      }
    })
    const data = (await userRawPromise.value)?.data
    setRaw(data)
  }

  const individualName = computed(() => raw.individual.individualName)
  const loyaltyCard = computed(() => raw.loyalty.loyaltyCards.card.find((c) => c.isLastActiveCard))

  const statusGift: ComputedRef<StatusGiftObject> = computed(() => {
    const statusGifts = raw.loyalty?.statusGifts || []
    const statusGift = statusGifts.find((e) => e.giftType === 'Default')
    const giftEligible = statusGift?.gifts?.[0]

    return {
      ...giftEligible,
      eligibility: statusGift?.eligibility,
      isEligible: statusGift?.eligibility === 'ELIGIBLE',
      isConsumed: !!giftEligible?.giftDate
    }
  })

  const allSigStatusGift: ComputedRef<StatusGiftObject> = computed(() => {
    const statusGifts = raw.loyalty?.statusGifts || []

    const statusGift = statusGifts.find((e) => e.giftType === 'AllSignatureBrazil')
    const giftEligible = statusGift?.gifts?.find(
      (gift) => !gift.cancelled && isAfterDateNow(gift.giftExpirationDate)
    )

    return {
      ...giftEligible,
      hasNodeStatusGift: !!giftEligible,
      isEligible: statusGift?.eligibility === 'ELIGIBLE',
      isConsumed: !!giftEligible?.beneficiaryPmid
    }
  })

  const hasAllSigStatusGift = computed(
    () => allSigStatusGift.value.isEligible && !allSigStatusGift.value.isConsumed
  )

  const hasStatusGiftDefault = computed(
    () => statusGift.value?.isEligible && !statusGift.value?.isConsumed
  )

  const operations = computed(() => raw.loyalty.operations?.operation || [])
  const memberInfo = computed(() => raw.loyalty.memberInfo)
  const balances = computed(() => raw.loyalty.balances)
  const rewardsPoints = computed(() => balances.value.nbPoints)
  const awardTypes = computed(() => raw.loyalty.awardTypes?.awardType)
  const subscriptionCards = computed(() => raw.loyalty?.subscriptionCards)
  const cardSupport = computed(() => raw.loyalty.loyaltyCards.cardSupport)

  const productTier = computed(() => loyaltyCard.value?.cardProduct?.productTier || '')
  const cardTierStatusRule = computed(() => loyaltyCard.value?.statusRule)
  const isGold = computed(
    () => loyaltyCard.value?.cardProduct.cardCodeTARS === CARD_CODE_TARS_ENUM.A3
  )
  const isPlatinum = computed(
    () => loyaltyCard.value?.cardProduct.cardCodeTARS === CARD_CODE_TARS_ENUM.A4
  )
  const isDiamond = computed(
    () => loyaltyCard.value?.cardProduct.cardCodeTARS === CARD_CODE_TARS_ENUM.A5
  )
  const isLimitless = computed(
    () => loyaltyCard.value?.cardProduct.cardCodeTARS === CARD_CODE_TARS_ENUM.A6
  )
  const isGoldOrAbove = computed(
    () => isGold.value || isPlatinum.value || isDiamond.value || isLimitless.value
  )
  const isCucaEligible = computed(
    () => isGoldOrAbove.value || userHasAtLeastOneSubscriptionCard.value
  )
  const getAvailableAwards = (type: AWARDS_ENUM) =>
    awardTypes.value?.find((award) => award.type === type)?.numberOfRemainingAwards || 0

  const availableSNUS = computed(() => getAvailableAwards(AWARDS_ENUM.SNU))
  const availableDINS = computed(() => getAvailableAwards(AWARDS_ENUM.DIN))
  const availableFairmonts = computed(() =>
    [AWARDS_ENUM.FCNI, AWARDS_ENUM.FDIN, AWARDS_ENUM.FRUC, AWARDS_ENUM.FSUC].reduce(
      (acc, cur) => acc + getAvailableAwards(cur),
      0
    )
  )

  const allPlusExplorerVouchers = computed(() => {
    return awardTypes.value?.find((award) => award.type === AWARDS_ENUM.APEV)?.awards?.award
  })

  const firstName = computed(() => capitalize(individualName.value?.firstName || ''))
  const lastName = computed(() => capitalize(individualName.value?.lastName || ''))
  const civility = computed(() => individualName.value?.civility || '')
  const username = computed(() => `${firstName.value} ${lastName.value}`)
  const emails = computed(() => {
    const mediums = raw.contactMediums?.contactMedium || []

    return {
      [CONTEXT_TYPE_ENUM.PERSONAL]: mediums.find(
        (medium) =>
          medium?.emailContact?.email && medium?.contextType === CONTEXT_TYPE_ENUM.PERSONAL
      ),
      [CONTEXT_TYPE_ENUM.PROFESSIONAL]: mediums.find(
        (medium) =>
          medium?.emailContact?.email && medium?.contextType === CONTEXT_TYPE_ENUM.PROFESSIONAL
      )
    }
  })

  const rawPhoneNumbers = computed(() => {
    const mediums = raw.contactMediums?.contactMedium || []
    const getPhoneNumberByContextType = (contextType: CONTEXT_TYPE_ENUM) => {
      const phoneNumberMediums = mediums.filter(
        (medium) => medium?.telephoneNumber?.phoneNumber && medium?.contextType === contextType
      )
      const phoneNumberWithLoginUsageType = phoneNumberMediums.find((medium) =>
        medium?.mediumUsages?.mediumUsage?.some(
          (usage) => usage?.usageType === USAGE_TYPE_PHONE_ENUM.LOGIN
        )
      )
      const primaryPhoneNumberMedium = phoneNumberMediums.find(
        (medium) => medium?.telephoneNumber?.isPrimary
      )
      const phoneNumberWithCellUsageType = phoneNumberMediums.find((medium) =>
        medium?.mediumUsages?.mediumUsage?.some(
          (usage) => usage?.usageType === USAGE_TYPE_PHONE_ENUM.CELL
        )
      )
      return (
        phoneNumberWithLoginUsageType ||
        primaryPhoneNumberMedium ||
        phoneNumberWithCellUsageType ||
        phoneNumberMediums?.[0]
      )
    }

    return {
      [CONTEXT_TYPE_ENUM.PERSONAL]: getPhoneNumberByContextType(CONTEXT_TYPE_ENUM.PERSONAL),
      [CONTEXT_TYPE_ENUM.PROFESSIONAL]: getPhoneNumberByContextType(CONTEXT_TYPE_ENUM.PROFESSIONAL)
    }
  })

  // We filter out numbers with '00000' for all front logic, db will be cleaned eventually
  const phoneNumbers = computed(() => {
    return {
      [CONTEXT_TYPE_ENUM.PERSONAL]:
        rawPhoneNumbers.value[CONTEXT_TYPE_ENUM.PERSONAL]?.telephoneNumber?.phoneNumber !== '00000'
          ? rawPhoneNumbers.value[CONTEXT_TYPE_ENUM.PERSONAL]
          : undefined,
      [CONTEXT_TYPE_ENUM.PROFESSIONAL]:
        rawPhoneNumbers.value[CONTEXT_TYPE_ENUM.PROFESSIONAL]?.telephoneNumber?.phoneNumber !==
        '00000'
          ? rawPhoneNumbers.value[CONTEXT_TYPE_ENUM.PROFESSIONAL]
          : undefined
    }
  })

  const mfaPhoneNumber = computed(() =>
    Object.values(phoneNumbers.value).find((medium) => {
      return medium?.mediumUsages?.mediumUsage?.some((usage) => {
        return usage?.usageType === USAGE_TYPE_PHONE_ENUM.LOGIN
      })
    })
  )

  const requestedPhone = computed(() =>
    Object.values(phoneNumbers.value).find((user) => user?.telephoneNumber?.requestedPhone)
  )

  const userPhoneNumberCount = computed(
    () =>
      [
        phoneNumbers.value[CONTEXT_TYPE_ENUM.PERSONAL]?.telephoneNumber,
        phoneNumbers.value[CONTEXT_TYPE_ENUM.PROFESSIONAL]?.telephoneNumber
      ].filter(Boolean).length
  )

  const proNpaiMedium = computed(() => {
    const contactMediums = raw.contactMediums?.contactMedium || []
    const mediumsNpai = contactMediums.filter(
      (medium) =>
        [CONTEXT_TYPE_ENUM.PROFESSIONAL, CONTEXT_TYPE_ADDRESS_ENUM.PRO].includes(
          medium.contextType
        ) && medium.isNpai
    )

    return mediumsNpai
      .map((medium) => {
        if (medium.emailContact) {
          return NPAI_ENUM.EMAIL
        }
        if (medium.postalAddress) {
          return NPAI_ENUM.ADDRESS
        }
        if (medium.telephoneNumber) {
          return NPAI_ENUM.PHONE
        }
      })
      .filter((medium) => !!medium)
  })

  const personalNpaiMedium = computed(() => {
    const contactMediums = raw.contactMediums?.contactMedium || []
    const mediumsNpai = contactMediums.filter(
      (medium) =>
        [CONTEXT_TYPE_ENUM.PERSONAL, CONTEXT_TYPE_ADDRESS_ENUM.PERSONAL].includes(
          medium.contextType
        ) && medium.isNpai
    )

    return mediumsNpai
      .map((medium) => {
        if (medium.emailContact) {
          return NPAI_ENUM.EMAIL
        }
        if (medium.postalAddress) {
          return NPAI_ENUM.ADDRESS
        }
        if (medium.telephoneNumber) {
          return NPAI_ENUM.PHONE
        }
      })
      .filter((medium) => !!medium)
  })

  const placeOfBirth = computed(() => raw.individual?.placeOfBirth || '')
  const birthDate = computed(() => raw.individual?.birthDate || '')
  const individualContracts = computed(() => raw.individual?.individualContracts)
  const nationality = computed(() => raw.individual?.nationality || '')
  const coBrandedCards = computed(() => raw.loyalty?.coBrandedCards?.coBrandedCard || [])
  const subscribedBonuses = computed(() => raw.loyalty?.subscribedBonuses?.subscribedBonus || [])
  const personalAddress = computed(() =>
    raw.contactMediums?.contactMedium.find(
      (contact) => contact.contextType === CONTEXT_TYPE_ADDRESS_ENUM.PERSONAL
    )
  )
  const personalAddressCountryCode = computed(
    () => personalAddress.value?.postalAddress?.countryCode
  )
  const proBillingAddress = computed(() =>
    raw.contactMediums?.contactMedium.find(
      (contact) =>
        contact.contextType === CONTEXT_TYPE_ADDRESS_ENUM.PRO &&
        contact.mediumUsages?.mediumUsage?.some(
          ({ usageType }) => usageType === USAGE_TYPE_ADDRESS_ENUM.BILLING
        )
    )
  )
  const proCommunicationAddress = computed(() => {
    return raw.contactMediums?.contactMedium.find(
      (contact) =>
        contact.contextType === CONTEXT_TYPE_ADDRESS_ENUM.PRO &&
        contact.mediumUsages?.mediumUsage?.some(
          ({ usageType }) => usageType === USAGE_TYPE_ADDRESS_ENUM.COMMUNICATION
        )
    )
  })

  const vat = computed(() => proBillingAddress.value?.postalAddress?.intraCommunityVat || '')
  const primaryAddress = computed(() => {
    const mediums = raw.contactMediums?.contactMedium || []
    const address = mediums.find((medium) => medium?.postalAddress?.isPrimary)
    return (
      address || {
        contextType: CONTEXT_TYPE_ADDRESS_ENUM.PERSONAL,
        postalAddress: {
          address1: '',
          zipCode: '',
          city: ''
        }
      }
    )
  })
  const hasEuroCurrency = computed(() => userCurrency.value === 'EUR')

  const setStatusGiftResponse = (statusGiftResponse?: CreateStatusGiftResponse) => {
    raw.statusGiftResponse = statusGiftResponse
  }

  const statusMatches = computed(() => raw.loyalty?.statusMatches)

  const onGoingStatusMatches = computed(() =>
    statusMatches.value?.find(
      (statusOffer) => statusOffer.status === 'ONGOING' && statusOffer.offerCompleted === false
    )
  )

  const statusMatchReached = computed(() =>
    statusMatches.value?.find(
      (statusOffer) => statusOffer.status === 'SUCCEEDED' && statusOffer.offerCompleted
    )
  )

  const isAllSignaturePartner = computed(
    () =>
      !!coBrandedCards.value.find(
        (card) => card.partnerCode === COBRAND_AIRLINES_PARTNER_CODE_ENUM.ACCOR_BRAZIL
      )
  )
  const hasWooriCard = computed(
    () =>
      !!coBrandedCards.value.find(
        (card) => card.partnerCode === COBRAND_AIRLINES_PARTNER_CODE_ENUM.WOORI
      )
  )
  const hasCimbNiagaCard = computed(
    () =>
      !!coBrandedCards.value.find(
        (card) => card.partnerCode === COBRAND_AIRLINES_PARTNER_CODE_ENUM.CIMB_NIAGA
      )
  )

  const allSignatureSubscriptions = computed(() => raw.loyalty?.allSignatureSubscriptions)
  const activeAllSignatureSubscription = computed(() => {
    if (!allSignatureSubscriptions.value?.length || !isAllSignaturePartner.value) return undefined
    const validStatusList = ['active', 'pending_cancellation', 'past_due']
    const activeSubscription = allSignatureSubscriptions.value.find(({ status }) =>
      validStatusList.includes(status)
    )
    return activeSubscription
  })

  const memberGift = computed(() => raw.loyalty?.memberGifts)

  const isNotEditableName = computed(() => {
    return !raw.individual?.isNameUpdateAllowed
  })

  const lastAuthenticationDate = computed(
    () => raw.authentication?.idpInformation?.lastAuthenticationDate
  )

  const lastPasswordUpdateDate = computed(
    () => raw.authentication?.idpInformation?.lastPasswordUpdateDate
  )

  const creditForNextStays = computed(() => raw.loyalty?.balances?.pointsAmountForStays)

  const last12MonthRewardsPoints = computed(
    () => raw.loyalty.balances.nbPointsEarnLast12Months || 0
  )

  const hasWallet = computed(() => raw.individual.hasWallet)

  const enum NEWSLETTER_SUBSCRIPTION_CODES {
    SGN = 'SGN',
    ALP = 'ALP',
    NOV = 'NOV'
  }

  const isAllSignatureNewsletterOptIn = computed(() => {
    const allSignatureSubscription =
      raw?.individual?.individualSubscriptions?.individualSubscription?.find(
        ({ code }) => code === NEWSLETTER_SUBSCRIPTION_CODES.SGN
      )
    return allSignatureSubscription?.optin
  })

  // These cards are the last active card of their type even if they are no longer active today
  const lastActiveSubscriptionCards = computed(() => {
    const maxNumberOfDaysAfterExpiration = 60
    return (
      subscriptionCards.value?.card.filter(
        (subscription) =>
          subscription.isLastActiveCard &&
          !isTodayAtLeastNDaysAfterToday(subscription.endDate, maxNumberOfDaysAfterExpiration)
      ) || []
    )
  })

  const allSignaturePlanCode = computed(() => {
    return activeAllSignatureSubscription.value?.plan_code || ALL_SIGNATURE_PLAN.SIGPL0
  })

  const changeToPricingOptionId = computed(
    () => activeAllSignatureSubscription.value?.change_to_pricing_option_id
  )

  const filterFreeNights = (status: FreeNightStatus) => {
    return (
      raw.loyalty?.freenights?.freenight?.filter((freenight) => freenight.status === status) ?? []
    )
  }

  const redeemableFreeNights = computed(() => filterFreeNights(FreeNightStatus.REDEEMABLE))

  const redeemedFreeNights = computed(() => filterFreeNights(FreeNightStatus.REDEEMED))

  const hasStayPlus = computed(() => {
    return redeemableFreeNights.value.length > 0 || redeemedFreeNights.value.length > 0
  })

  function hasActiveCard(code: string, product: string) {
    return computed(() =>
      (subscriptionCards.value?.card ?? []).some(
        (card: SubscriptionCard) =>
          card.isLastActiveCard &&
          card.cardProduct?.cardCodeTARS === code &&
          card.cardProduct?.productCode === product
      )
    )
  }

  const hasAllPlusIbis = hasActiveCard(IBIS_CARD.code, IBIS_CARD.product)
  const hasAllPlusVoyageur = hasActiveCard(ALL_VOYAGEUR_CARD.code, ALL_VOYAGEUR_CARD.product)
  const hasAllPlusExplorer = hasActiveCard(ALL_EXPLORER_CARD.code, ALL_EXPLORER_CARD.product)

  const isAllPlusExplorer = (subscription: SubscriptionCard) => {
    const { cardCodeTARS, productCode } = subscription.cardProduct
    return (
      cardCodeTARS === CARD_CODE_TARS_ENUM.G7 &&
      productCode === ALL_PLUS_PRODUCT_CODE.ALL_PLUS_EXPLORER
    )
  }

  const isAllPlusIbis = (subscription: SubscriptionCard) => {
    const { cardCodeTARS, productCode } = subscription.cardProduct
    return (
      cardCodeTARS === CARD_CODE_TARS_ENUM.FI && productCode === ALL_PLUS_PRODUCT_CODE.ALL_PLUS_IBIS
    )
  }

  const isAllPlusVoyageur = (subscription: SubscriptionCard) => {
    const { cardCodeTARS, productCode } = subscription.cardProduct
    return (
      cardCodeTARS === CARD_CODE_TARS_ENUM.B3 &&
      productCode === ALL_PLUS_PRODUCT_CODE.ALL_PLUS_VOYAGEUR
    )
  }

  const getSubscriptionCardByName = (name: SUBSCRIPTION_NAME) => {
    if (name === SUBSCRIPTION_NAME.ALL_SIGNATURE) {
      return activeAllSignatureSubscription.value
    } else {
      const subscriptionConditions = {
        [SUBSCRIPTION_NAME.ALLPLUS_IBIS]: isAllPlusIbis,
        [SUBSCRIPTION_NAME.ALLPLUS_EXPLORER]: isAllPlusExplorer,
        [SUBSCRIPTION_NAME.ALLPLUS_VOYAGEUR]: isAllPlusVoyageur
      }
      return lastActiveSubscriptionCards.value.find((subscription: SubscriptionCard) =>
        subscriptionConditions[name]?.(subscription)
      )
    }
  }

  const userHasAtLeastOneSubscriptionCard = computed(
    () => !!activeAllSignatureSubscription.value || lastActiveSubscriptionCards.value.length > 0
  )

  const individualLegalIdentification = computed(
    () => raw.individual?.individualLegalIdentifications?.individualLegalIdentification || []
  )

  return {
    pmid,
    userCurrency,
    setUserCurrency,
    emails,
    phoneNumbers,
    mfaPhoneNumber,
    userPhoneNumberCount,
    user,
    setUser,
    raw,
    setRaw,
    firstName,
    lastName,
    username,
    nationality,
    operations,
    balances,
    loyaltyCard,
    cardSupport,
    memberInfo,
    productTier,
    availableSNUS,
    availableDINS,
    availableFairmonts,
    coBrandedCards,
    subscribedBonuses,
    hasEuroCurrency,
    statusGift,
    allSigStatusGift,
    hasAllSigStatusGift,
    hasStatusGiftDefault,
    setStatusGiftResponse,
    awardTypes,
    allPlusExplorerVouchers,
    primaryAddress,
    onGoingStatusMatches,
    statusMatchReached,
    activeAllSignatureSubscription,
    memberGift,
    cardTierStatusRule,
    refreshUser,
    rewardsPoints,
    lastAuthenticationDate,
    lastPasswordUpdateDate,
    civility,
    personalAddress,
    personalAddressCountryCode,
    proCommunicationAddress,
    proBillingAddress,
    placeOfBirth,
    isNotEditableName,
    birthDate,
    individualContracts,
    vat,
    proNpaiMedium,
    personalNpaiMedium,
    creditForNextStays,
    last12MonthRewardsPoints,
    hasWallet,
    isAllSignatureNewsletterOptIn,
    lastActiveSubscriptionCards,
    allSignaturePlanCode,
    requestedPhone,
    rawPhoneNumbers,
    changeToPricingOptionId,
    redeemableFreeNights,
    redeemedFreeNights,
    userRawPromise,
    isAllPlusExplorer,
    isAllPlusIbis,
    isAllPlusVoyageur,
    getSubscriptionCardByName,
    hasAllPlusIbis,
    hasAllPlusVoyageur,
    hasAllPlusExplorer,
    hasStayPlus,
    userHasAtLeastOneSubscriptionCard,
    hasWooriCard,
    hasCimbNiagaCard,
    individualLegalIdentification,
    isGold,
    isPlatinum,
    isDiamond,
    isLimitless,
    isGoldOrAbove,
    isCucaEligible
  }
})
